/**
 * The open tabs' model (P-30, A-#284, A-#288; Arthur, 29 October 2026). A tab is a CLIENT or a PAGE. A client tab is
 * the client's id; a page tab is one of PAGE_TABS, a fixed list whose labels are words, never data. Pure functions,
 * so the strip (components/OpenTabs.tsx) and the tests share one rule. A page joins the strip by a line in PAGE_TABS
 * and nothing else.
 *
 * What is stored, per staff id in sessionStorage, is ids only: a client's uuid, or "page:<key>". Never a name, a
 * label or anything a page holds; a page's state is kept in memory only (OpenTabs' kept store).
 */

export const PAGE_TABS = {
  triage: { path: '/tasks/triage', label: 'Triage' },
  myTime: { path: '/time/mine', label: 'My time' },
  departmentTime: { path: '/time/department', label: 'Department time' },
} as const;

export type PageKey = keyof typeof PAGE_TABS;

export type Tab = { kind: 'client'; id: string } | { kind: 'page'; page: PageKey };

/** At most this many CLIENT tabs, the oldest dropped; a page tab is at most one of each, outside the count. */
export const MAX_CLIENT_TABS = 8;

export const CLIENT_PATH = /^\/clients\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(\/|$)/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PAGE_PREFIX = 'page:';

function isPageKey(k: string): k is PageKey {
  return Object.prototype.hasOwnProperty.call(PAGE_TABS, k);
}

/** The tab a path belongs to: a client's record at any depth, or a page tab's exact path; otherwise none. */
export function tabForPath(pathname: string): Tab | null {
  const m = CLIENT_PATH.exec(pathname);
  if (m) return { kind: 'client', id: m[1]!.toLowerCase() };
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  for (const k of Object.keys(PAGE_TABS) as PageKey[]) if (PAGE_TABS[k].path === path) return { kind: 'page', page: k };
  return null;
}

export function sameTab(a: Tab, b: Tab): boolean {
  return a.kind === 'client' ? b.kind === 'client' && a.id === b.id : b.kind === 'page' && a.page === b.page;
}

/** A tab's key: its stored form, and the React key. */
export function tabKey(t: Tab): string {
  return t.kind === 'client' ? t.id : `${PAGE_PREFIX}${t.page}`;
}

export function tabPath(t: Tab): string {
  return t.kind === 'client' ? `/clients/${t.id}` : PAGE_TABS[t.page].path;
}

/** Adds a tab once, at the end; past MAX_CLIENT_TABS the oldest CLIENT tab goes, never a page tab. */
export function openTab(list: readonly Tab[], tab: Tab): Tab[] {
  if (list.some((t) => sameTab(t, tab))) return [...list];
  const out = [...list, tab];
  let clients = out.filter((t) => t.kind === 'client').length;
  while (clients > MAX_CLIENT_TABS) {
    out.splice(out.findIndex((t) => t.kind === 'client'), 1);
    clients--;
  }
  return out;
}

/**
 * Closes a tab. `goTo` is where to go when the closed tab was the one on screen: the last tab left, or, with none,
 * the client list for a client and My tasks for a page.
 */
export function closeTab(list: readonly Tab[], tab: Tab): { list: Tab[]; goTo: string } {
  const out = list.filter((t) => !sameTab(t, tab));
  const last = out.at(-1);
  return { list: out, goTo: last ? tabPath(last) : tab.kind === 'client' ? '/clients' : '/tasks' };
}

/** What is written: ids and page keys only. */
export function serialise(list: readonly Tab[]): string {
  return JSON.stringify(list.map(tabKey));
}

/** What is read: anything that is neither a uuid nor a known page key is dropped; then the same limits as open. */
export function parseStored(raw: string | null): Tab[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  let out: Tab[] = [];
  for (const x of parsed) {
    if (typeof x !== 'string') continue;
    if (UUID.test(x)) out = openTab(out, { kind: 'client', id: x.toLowerCase() });
    else if (x.startsWith(PAGE_PREFIX) && isPageKey(x.slice(PAGE_PREFIX.length))) {
      out = openTab(out, { kind: 'page', page: x.slice(PAGE_PREFIX.length) as PageKey });
    }
  }
  return out;
}

/** The session keys: the tabs since P-30, and the client-only list before it, read once and removed. */
export const storageKey = (staffId: string) => `procura.openTabs.${staffId}`;
export const oldStorageKey = (staffId: string) => `procura.openClients.${staffId}`;
